// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import SkeletonLineView from '@tcp/core/src/components/common/molecules/LoaderSkelton/views/SkeletonLine.view';
import { getLabelFromCopyOrFromTranslation } from '@tcp/core/src/utils/labels/labelUtilty';
import { PLPLabels } from '@tcp/core/src/utils/labels/browse/PLP.labels';
import { capitalizeWords } from '@tcp/core/src/utils/utils';
import { replaceWord } from '@tcp/web/src/components/features/browse/CategoryListingPage/clpContainerHelper';
import useHasMounted from '@tcp/core/src/hooks/useHasMounted';
import { getViewportInfo } from '@tcp/core/src/utils';
import {
  Container,
  RenderText,
  SeoTitle,
  SeoTitleSkeleton,
} from '../styles/SeoHeaderSection.style';

const colorAndSizePatternReplacement = ({
  isFacetedNavigationEnabled,
  seoTitle,
  appliedFilterIdsAndLength = {},
}) => {
  const {
    filterCount,
    filterCountFromURL,
    singleColorFilterValue = '',
    singleSizeFilterValue = '',
  } = appliedFilterIdsAndLength;
  const sizeLabel = getLabelFromCopyOrFromTranslation('PLP.PLP_sort_filter.lbl_size', PLPLabels);
  const lsl = sizeLabel?.toLowerCase();

  let updatedSeoTitle = seoTitle;

  const patternsToRemove = ['<color> ', '<c_color> ', '<size> ', '<s_size> '];

  patternsToRemove.forEach((pattern) => {
    updatedSeoTitle = replaceWord(updatedSeoTitle, pattern, '');
  });

  if (isFacetedNavigationEnabled && (filterCount === 1 || filterCountFromURL === 1)) {
    if (singleColorFilterValue) {
      updatedSeoTitle = replaceWord(seoTitle, '<color>', singleColorFilterValue.toLowerCase());
      updatedSeoTitle = replaceWord(
        updatedSeoTitle,
        '<c_color>',
        capitalizeWords(singleColorFilterValue)
      );
      updatedSeoTitle = replaceWord(updatedSeoTitle, '<size> ', '');
      updatedSeoTitle = replaceWord(updatedSeoTitle, '<s_size> ', '');
    }
    if (singleSizeFilterValue) {
      updatedSeoTitle = replaceWord(seoTitle, '<size>', `${lsl} ${singleSizeFilterValue}`);
      updatedSeoTitle = replaceWord(
        updatedSeoTitle,
        ' <s_size>',
        `: ${sizeLabel} ${singleSizeFilterValue}`
      );
      updatedSeoTitle = replaceWord(updatedSeoTitle, '<color> ', '');
      updatedSeoTitle = replaceWord(updatedSeoTitle, '<c_color> ', '');
    }
  }

  return updatedSeoTitle;
};

const seoH1fix = (viewMode, hasMounted, isDesktop, updatedSeoHeaderCopy) => {
  let newSeoHeaderCopy = updatedSeoHeaderCopy;
  if (viewMode === 'desktop') {
    if (hasMounted && isDesktop) {
      newSeoHeaderCopy = newSeoHeaderCopy.replace('<p>', '<h1>');
      newSeoHeaderCopy = newSeoHeaderCopy.replace('</p>', '</h1>');
    }
    if (hasMounted && !isDesktop) {
      newSeoHeaderCopy = newSeoHeaderCopy.replace('<h1>', '<p>');
      newSeoHeaderCopy = newSeoHeaderCopy.replace('</h1>', '</p>');
    }
    if (!hasMounted) {
      newSeoHeaderCopy = newSeoHeaderCopy.replace('<h1>', '<p>');
      newSeoHeaderCopy = newSeoHeaderCopy.replace('</h1>', '</p>');
    }
  } else if (hasMounted && isDesktop) {
    newSeoHeaderCopy = newSeoHeaderCopy.replace('<h1>', '<p>');
    newSeoHeaderCopy = newSeoHeaderCopy.replace('</h1>', '</p>');
  }

  return newSeoHeaderCopy;
};

const SeoHeaderSection = ({
  isSeoHeaderCopyLoaded,
  seoHeaderCopy,
  viewMode,
  addExtraMarginLeft,
  isFacetedNavigationEnabled,
  appliedFilterIdsAndLength,
  isPaginatedPlp,
}) => {
  const hasMounted = useHasMounted();
  const { isDesktop } = getViewportInfo() || {};
  const isWrappedWithTag =
    !isEmpty(seoHeaderCopy) && seoHeaderCopy.startsWith('<h1>') && seoHeaderCopy.endsWith('</h1>');
  const seoHeaderCopyData = isWrappedWithTag ? seoHeaderCopy : `<h1>${seoHeaderCopy}</h1>`;

  let updatedSeoHeaderCopy = colorAndSizePatternReplacement({
    isFacetedNavigationEnabled,
    seoTitle: seoHeaderCopyData,
    appliedFilterIdsAndLength,
  });

  const overrideSkeletonStyle = {
    height: 22,
    marginBottom: 4,
  };

  if (isPaginatedPlp) {
    updatedSeoHeaderCopy = seoH1fix(viewMode, hasMounted, isDesktop, updatedSeoHeaderCopy);

    return isSeoHeaderCopyLoaded ? (
      <SeoTitle
        className="category-title"
        richTextHtml={updatedSeoHeaderCopy}
        viewMode={viewMode}
      />
    ) : (
      <SeoTitleSkeleton viewMode={viewMode}>
        <SkeletonLineView className="category-title-skeleton" />
      </SeoTitleSkeleton>
    );
  }

  if (isSeoHeaderCopyLoaded) {
    return !isEmpty(seoHeaderCopy) ? (
      <Container
        className={`new-seo-Header-container_${viewMode}`}
        viewMode={viewMode}
        addExtraMarginLeft={addExtraMarginLeft}
      >
        <RenderText
          className={`new-seo-header-text-section_${viewMode}`}
          richTextHtml={updatedSeoHeaderCopy}
        />
      </Container>
    ) : (
      <></>
    );
  }

  return (
    <Container
      className={`new-seo-Header-skeleton-container_${viewMode}`}
      viewMode={viewMode}
      isSeoHeaderCopyEmpty={!isEmpty(seoHeaderCopy)}
      addExtraMarginLeft={addExtraMarginLeft}
      isSkeletonView
    >
      <SkeletonLineView
        className="skeleton-view"
        width={'34%'}
        inheritedStyles={overrideSkeletonStyle}
      />
    </Container>
  );
};

SeoHeaderSection.propTypes = {
  viewMode: PropTypes.string.isRequired,
  isSeoHeaderCopyLoaded: PropTypes.bool.isRequired,
  seoHeaderCopy: PropTypes.string,
  addExtraMarginLeft: PropTypes.bool,
  isFacetedNavigationEnabled: PropTypes.bool,
  appliedFilterIdsAndLength: PropTypes.shape({}),
  isPaginatedPlp: PropTypes.bool,
};

SeoHeaderSection.defaultProps = {
  seoHeaderCopy: '',
  addExtraMarginLeft: false,
  isFacetedNavigationEnabled: false,
  appliedFilterIdsAndLength: {},
  isPaginatedPlp: false,
};

export default SeoHeaderSection;
export { SeoHeaderSection as SeoHeaderSectionVanilla };
